import { useState, useContext, createContext, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { isFreemium } from "src/modules/auth/duck/selectors";

import PopoverView from "src/components/PopoverView";
import PurchaseFlow from "src/modules/purchase/PurchaseFlow";

const PurchaseContext = createContext();

export function usePurchaseContext() {
  return useContext(PurchaseContext);
}

export function withPurchase(Component) {
  return (props) => {
    const purchase = usePurchaseContext();
    return <Component purchase={purchase} {...props} />;
  };
}

const defaultStyle = {
  bg: "surfaceElevated1",
  boxShadow: "elevation2"
};

function ContextProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [props, setProps] = useState(null);
  const [style, setStyle] = useState(defaultStyle);

  const isFreeVersion = useSelector(isFreemium);

  function updateStyle(style) {
    if (!isFreeVersion) return;
    setStyle((prevState) => ({ ...prevState, ...style }));
  }

  useEffect(() => {
    if (!content) setStyle(defaultStyle);
  }, [content]);

  function open({ content, props }) {
    if (content) {
      setStyle({
        bg: "transparent",
        boxShadow: "none"
      });
    } else setStyle(defaultStyle);

    setContent(content);
    setProps(props);
    setIsOpen(true);
  }

  function close() {
    setIsOpen(false);
  }

  const purchase = useMemo(() => ({ open, setContent }), []);

  return (
    <PurchaseContext.Provider value={purchase}>
      <PopoverView bodyProps={style} isOpen={isOpen} onClose={close}>
        <PurchaseFlow
          onClose={close}
          freemiumInitialContent={content}
          updatePopoverStyle={updateStyle}
          setContent={setContent}
          {...props}
        />
      </PopoverView>
      {children}
    </PurchaseContext.Provider>
  );
}

export default ContextProvider;
